import React, { useEffect, useRef, useState } from "react";
import FormContents from "components/atoms/Form/FormContents";
import FormField from "components/atoms/Form/FormField";
import BlockUI from "components/molecules/BlockUi";
import GenericTable from "components/organismos/GenericTable/GenericTable";
import SearchPanel from "components/organismos/SearchPanel/SearchPanel";
import AccordionPanel from "components/molecules/AccordionPanel";
import { grayScale } from "components/styles";
import SelectForm from "components/molecules/SelectForm";
import useCustomReport, {
  useLayoutOptions
} from "components/organismos/master/storeManagement/report/CustomizeReportLayout/hooks";
import { useSelector } from "react-redux";
import { Store } from "modules/store";
import { useHistory, useLocation } from "react-router-dom";

/** セレクトボックスで帳票を選ぶ従来版の画面パス */
const SELECT_MODE_PATH = "/customizedReport";

const CustomizeReportLayout: React.FC<{
  onTitleChange?: (title: string) => void;
}> = ({ onTitleChange }) => {
  const [layoutId, setLayoutId] = useState("");
  const [isExpand, setExpand] = useState(true);
  const [searchField, setSearchField] = useState<any>([]);

  const roleScreen = useSelector((state: Store) => state.auth.roleScreen);
  const history = useHistory();
  const { pathname } = useLocation();

  // /customizedReport 完全一致のときのみセレクト版。
  // それ以外（汎用パス /customizedReport/{layoutCode} と独自パス）は独立メニューとして
  // 画面パスから帳票を1件解決する。
  const standalonePath = pathname === SELECT_MODE_PATH ? undefined : pathname;

  const { layoutOptions, loading, notFound, searchFields } = useLayoutOptions(
    standalonePath
  );

  // 独立メニューは帳票が1件に確定するため自動選択する
  useEffect(() => {
    if (!standalonePath || layoutOptions.length === 0) {
      return;
    }
    const target = layoutOptions[0];
    setLayoutId(target.value);
    setSearchField(searchFields[target.value] || []);
    if (onTitleChange) {
      onTitleChange(target.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalonePath, layoutOptions, searchFields]);

  // メニューは存在するが帳票が紐づいていない場合は404へ
  useEffect(() => {
    if (notFound) {
      history.push("/404");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound]);

  const {
    headerRows,
    dataRows,
    handleSearchChange,
    handleDownloadCSV,
    handleDownloadExcel,
    loading: loadingReport
  } = useCustomReport(
    layoutId,
    layoutOptions.find(option => option.value === layoutId)?.label || "",
    searchFields[layoutId]
  );

  const searchPanelRef = useRef<HTMLDivElement>(null);
  const [searchPanelHeight, setSearchPanelHeight] = useState<number>(56);

  useEffect(() => {
    if (isExpand === false) {
      setSearchPanelHeight(56);
      return;
    }
    if (searchPanelRef.current) {
      const height = searchPanelRef.current.getBoundingClientRect().height;
      setSearchPanelHeight(height);
    }
  }, [layoutId, isExpand]);

  return (
    <BlockUI blocking={loading || loadingReport}>
      <FormContents>
        {!standalonePath && (
          <FormField displayBlock={true}>
            <SelectForm
              label="帳票名"
              name=""
              value={layoutId}
              setValue={v => {
                setLayoutId(v);
                setSearchField(searchFields[v] || []);
              }}
              options={layoutOptions}
            />
          </FormField>
        )}

        <div>
          {layoutId && (
            <div className="row">
              {/* 検索パネル */}
              <div ref={searchPanelRef}>
                <AccordionPanel
                  key={layoutId}
                  text="検索条件"
                  color={grayScale.gray100}
                  callBack={isExpand => setExpand(isExpand)}
                  defaultExpand={isExpand}
                >
                  <SearchPanel<any>
                    searchFields={searchField}
                    onSearchChange={handleSearchChange}
                  />
                </AccordionPanel>
              </div>
              <div style={{ marginTop: "8px" }}>
                <GenericTable
                  key={layoutId}
                  maxHeight={`calc(100svh - ${searchPanelHeight}px - 110px)`}
                  headerRows={headerRows}
                  dataRows={dataRows}
                  sortable={true}
                  showExportExcel={roleScreen && roleScreen.downloadFlag === 1}
                  showExportCSV={roleScreen && roleScreen.downloadFlag === 1}
                  onExportCSV={handleDownloadCSV}
                  onExportExcel={handleDownloadExcel}
                />
              </div>
            </div>
          )}
        </div>
      </FormContents>
    </BlockUI>
  );
};

export default CustomizeReportLayout;
